import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@shared/services/api';
import { formatBytes } from '@/shared/utils/formatBytes';
import { Card, Badge, Button } from '@shared/components/ui';
import {
  Database,
  ArrowLeft,
  PieChart,
  HardDrive,
  FileText,
  Calendar,
  TrendingUp,
  TrendingDown,
  Eye,
} from 'lucide-react';

interface BackupSizeInfo {
  id: string;
  name: string;
  status: string;
  sizeBytes: number;
  sizeGB: number;
  createdAt: string;
  completedAt: string;
}

interface StorageBreakdownData {
  backups: BackupSizeInfo[];
  totalSize: number;
  totalSizeGB: number;
  backupCount: number;
}

interface StorageUsage {
  storage_used_bytes: number;
  storage_used_gb: number;
  storage_quota_gb: number;
  storage_usage_percent: number;
}

export default function StorageBreakdown() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('storage');
  const locale = i18n.resolvedLanguage || i18n.language;
  const [data, setData] = useState<StorageBreakdownData | null>(null);
  const [savings, setSavings] = useState<{
    original_size_gb: number;
    actual_usage_gb: number;
    savings_gb: number;
    savings_percent: number;
  } | null>(null);
  const [storageUsage, setStorageUsage] = useState<StorageUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    fetchStorageBreakdown();
    fetchSavings();
    fetchStorageUsage();
  }, []);

  const fetchStorageBreakdown = async () => {
    try {
      setLoading(true);
      setLoadFailed(false);
      const res = await api.get('/billing/storage-breakdown');
      setData(res.data);
    } catch {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (value: number, maximumFractionDigits = 1) =>
    new Intl.NumberFormat(locale, {
      maximumFractionDigits,
      minimumFractionDigits: maximumFractionDigits,
    }).format(value);
  const formatDate = (value: string) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? t('breakdown.table.unknownDate')
      : new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(date);
  };
  const formatStatus = (status: string) => {
    const key = `breakdown.status.${status.toLowerCase()}`;
    const translated = t(key);
    return translated === key ? status : translated;
  };

  const fetchSavings = async () => {
    try {
      const res = await api.get('/storage/savings');
      setSavings(res.data);
    } catch {
      setSavings(null);
    }
  };

  const fetchStorageUsage = async () => {
    try {
      const res = await api.get('/storage/usage');
      setStorageUsage(res.data);
    } catch {
      setStorageUsage(null);
    }
  };

  return (
    <>
      <div className="space-y-6 animate-in fade-in duration-300">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-5">
          <div className="flex items-start gap-3 sm:items-center sm:gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              aria-label={t('breakdown.back')}
              className="min-h-11 min-w-11 p-2 hover:bg-surface-2-light dark:hover:bg-surface-2-dark rounded-xl"
            >
              <ArrowLeft
                size={20}
                aria-hidden="true"
                className="text-text-subtle-light dark:text-text-subtle-dark"
              />
            </Button>
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-text-primary-light dark:text-text-primary-dark tracking-tight">
                {t('breakdown.title')}
              </h1>
              <p className="text-sm sm:text-base text-text-muted-light dark:text-text-muted-dark mt-1">
                {t('breakdown.subtitle')}
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center" role="status" aria-live="polite">
            <div className="animate-spin h-10 w-10 border-b-2 border-brand-600 rounded-full mx-auto mb-4"></div>
            <p className="text-xs font-black uppercase tracking-widest text-text-subtle-light dark:text-text-subtle-dark">
              {t('breakdown.loading')}
            </p>
          </div>
        ) : loadFailed ? (
          <div role="alert">
            <Card className="flex flex-col items-start gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-text-primary-light dark:text-text-primary-dark">
                {t('breakdown.errors.loadFailed')}
              </p>
              <Button type="button" variant="secondary" onClick={fetchStorageBreakdown}>
                {t('breakdown.retry')}
              </Button>
            </Card>
          </div>
        ) : data && data.backups && data.backups.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                label={t('breakdown.stats.totalFootprint')}
                value={formatBytes(data.totalSize, locale)}
                icon={<HardDrive />}
                color="text-state-info-light dark:text-state-info-dark"
                bg="bg-state-info-bg-light dark:bg-state-info-bg-dark"
              />
              <StatCard
                label={t('breakdown.stats.backupUnits')}
                value={data.backupCount.toString()}
                icon={<Database />}
                color="text-state-info-light dark:text-state-info-dark"
                bg="bg-state-info-bg-light dark:bg-state-info-bg-dark"
              />
              <StatCard
                label={t('breakdown.stats.meanSnapshot')}
                value={formatBytes(data.totalSize / (data.backupCount || 1), locale)}
                icon={<PieChart />}
                color="text-state-ok-light dark:text-state-ok-dark"
                bg="bg-state-ok-bg-light dark:bg-state-ok-bg-dark"
              />
              {storageUsage && storageUsage.storage_quota_gb > 0 && (
                <StatCard
                  label={t('breakdown.stats.storageAvailable')}
                  value={`${formatNumber(storageUsage.storage_quota_gb - storageUsage.storage_used_gb)} GB`}
                  icon={<HardDrive />}
                  color="text-state-warn-light dark:text-state-warn-dark"
                  bg="bg-state-warn-bg-light dark:bg-state-warn-bg-dark"
                />
              )}
              {savings && savings.savings_gb > 0 && (
                <StatCard
                  label={t('breakdown.stats.dedupSavings')}
                  value={`${formatNumber(savings.savings_gb, 2)} GB (${formatNumber(savings.savings_percent, 0)}%)`}
                  icon={<TrendingDown />}
                  color="text-state-ok-light dark:text-state-ok-dark"
                  bg="bg-state-ok-bg-light dark:bg-state-ok-bg-dark"
                />
              )}
            </div>

            <Card className="overflow-hidden">
              <div className="p-6 bg-surface-2-light dark:bg-surface-2-dark border-b border-surface-border-light dark:border-surface-border-dark">
                <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
                  {t('breakdown.table.title')}
                </h2>
              </div>
              <div className="divide-y divide-surface-border-light dark:divide-surface-border-dark">
                {data.backups.map((backup, idx) => (
                  <div
                    key={backup.id}
                    className="p-4 sm:p-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between group hover:bg-surface-2-light dark:hover:bg-surface-2-dark transition-colors"
                  >
                    <div className="flex min-w-0 items-start gap-4 sm:items-center sm:gap-6">
                      <span className="text-xl sm:text-2xl font-bold text-slate-300 dark:text-slate-600">
                        {(idx + 1).toString().padStart(2, '0')}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-text-primary-light dark:text-text-primary-dark flex min-w-0 items-start gap-2 sm:items-center">
                          <FileText size={16} className="text-brand-600 dark:text-brand-400" />
                          <span className="min-w-0 break-words">
                            {backup.name || t('breakdown.table.anonymousJob')}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/backups/configs/${backup.id}/snapshots`);
                            }}
                            aria-label={t('breakdown.table.viewSnapshots', {
                              name: backup.name || t('breakdown.table.anonymousJob'),
                            })}
                            className="ml-auto inline-flex min-h-11 min-w-11 flex-shrink-0 items-center justify-center rounded-lg hover:bg-surface-2-light dark:hover:bg-surface-2-dark transition-colors opacity-100 sm:ml-1 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100"
                            title={t('breakdown.table.viewSnapshots', {
                              name: backup.name || t('breakdown.table.anonymousJob'),
                            })}
                          >
                            <Eye
                              size={14}
                              className="text-text-subtle-light dark:text-text-subtle-dark hover:text-brand-600 dark:hover:text-brand-400"
                            />
                          </button>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs font-bold text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest">
                          <span className="flex items-center gap-1">
                            <Calendar size={12} /> {formatDate(backup.completedAt)}
                          </span>
                          <Badge variant="neutral" className="text-[9px]">
                            {formatStatus(backup.status)}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    <div className="pl-10 text-left sm:pl-0 sm:text-right">
                      <div className="text-lg sm:text-xl font-bold text-text-primary-light dark:text-text-primary-dark">
                        {formatBytes(backup.sizeBytes, locale)}
                      </div>
                      <div className="flex items-center justify-end gap-1.5 text-[10px] font-black uppercase tracking-widest text-text-subtle-light dark:text-text-subtle-dark mt-1">
                        <TrendingUp
                          size={10}
                          className="text-state-ok-light dark:text-state-ok-dark"
                        />
                        {formatNumber((backup.sizeBytes / data.totalSize) * 100)}%{' '}
                        {t('breakdown.table.usage')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </>
        ) : (
          <Card className="py-24 text-center border-2 border-dashed">
            <div className="w-20 h-20 bg-surface-2-light dark:bg-surface-2-dark rounded-3xl flex items-center justify-center mx-auto mb-6 text-slate-300 dark:text-slate-600">
              <PieChart size={40} />
            </div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white">
              {t('breakdown.empty.title')}
            </h3>
            <p className="text-text-muted-light dark:text-text-muted-dark mt-2 max-w-sm mx-auto font-medium text-sm">
              {t('breakdown.empty.description')}
            </p>
            <Button variant="outline" className="mt-8" onClick={() => navigate('/dashboard')}>
              {t('breakdown.empty.returnToOverview')}
            </Button>
          </Card>
        )}
      </div>
    </>
  );
}

function StatCard({ icon, label, value, color, bg }: any) {
  return (
    <Card className={`p-6 flex items-center justify-between ${bg} border-none`}>
      <div>
        <p className="text-3xl font-black text-slate-900 dark:text-white">{value}</p>
        <p className={`text-[10px] font-black uppercase tracking-[0.2em] ${color} mt-1`}>{label}</p>
      </div>
      <div className={`p-3 rounded-2xl bg-white/50 dark:bg-surface-2-dark ${color}`}>{icon}</div>
    </Card>
  );
}
